import type { LinkProps } from "react-router";
import { FiCheck, FiChevronRight } from "react-icons/fi";
import { Avatar } from "~/components/Avatar";
import { PillButton } from "~/components/PillButton";
import { SectionHeading } from "~/components/SectionHeading";
import { Text } from "~/components/Text";
import { SetAction } from "~/hooks/use-action";
import { useRootLocale } from "~/root";
import { formatOnlyPlacesLeft, formatPlacesLeft } from "~/utils/localization";
import { PlanActions } from "./route";

interface GuestListProps {
  attendees: Array<{
    node: {
      id: string;
      user: {
        id: string;
        firstName: string;
        profileImageUrl: string;
      };
    };
  }>;
  unclaimedGuestCount?: number;
  unclaimedGuests?: Array<{ id: string; name: string }>;
  isTicketed: boolean;
  isAttending: boolean;
  guestListExposed: boolean;
  atCapacity: boolean;
  waitlistsEnabled: boolean;
  waitlistSize: number;
  capacity: number;
  onSetAction: SetAction<PlanActions>;
  ticketsLink?: LinkProps["to"];
  ticketsDisabled?: boolean;
  onRsvp?: () => void;
  onJoinWaitlist?: () => void;
  onSeeOtherPlans?: () => void;
  className?: string;
}

type FakeProfile = { src: string; name: string };

const FAKE_PROFILES: FakeProfile[] = [
  { src: "/images/fake-profiles/fake-profile-1.png", name: "Tessa L." },
  { src: "/images/fake-profiles/fake-profile-2.png", name: "Sabrina C." },
  { src: "/images/fake-profiles/fake-profile-3.png", name: "Daniel R." },
  { src: "/images/fake-profiles/fake-profile-4.png", name: "Maya P." },
  { src: "/images/fake-profiles/fake-profile-5.png", name: "Jordan K." },
  { src: "/images/fake-profiles/fake-profile-6.png", name: "Alex W." },
  { src: "/images/fake-profiles/fake-profile-7.png", name: "Priya M." },
  { src: "/images/fake-profiles/fake-profile-8.png", name: "Noah T." },
  { src: "/images/fake-profiles/fake-profile-9.png", name: "Ellie R." },
];

export function GuestList({
  attendees,
  unclaimedGuestCount = 0,
  unclaimedGuests = [],
  isTicketed,
  isAttending,
  guestListExposed,
  atCapacity,
  waitlistsEnabled,
  waitlistSize,
  capacity,
  onSetAction,
  ticketsLink,
  ticketsDisabled = false,
  onRsvp,
  onJoinWaitlist,
  onSeeOtherPlans,
  className = "",
}: GuestListProps) {
  const isHidden = isTicketed && !isAttending && !guestListExposed;

  if (!isTicketed && attendees.length === 0) return null;

  const handleViewAll = () =>
    onSetAction(undefined, {
      type: isHidden ? "private-guest-list" : "download-app",
      data: undefined,
    });

  return (
    <InlineGuestList
      attendees={attendees}
      unclaimedGuestCount={unclaimedGuestCount}
      unclaimedGuests={unclaimedGuests}
      isTicketed={isTicketed}
      isHidden={isHidden}
      isAttending={isAttending}
      atCapacity={atCapacity}
      waitlistsEnabled={waitlistsEnabled}
      waitlistSize={waitlistSize}
      capacity={capacity}
      onViewAll={handleViewAll}
      ticketsLink={ticketsLink}
      ticketsDisabled={ticketsDisabled}
      onRsvp={onRsvp}
      onJoinWaitlist={onJoinWaitlist}
      onSeeOtherPlans={onSeeOtherPlans}
      className={className}
    />
  );
}

type InlineGuestListProps = {
  attendees: GuestListProps["attendees"];
  unclaimedGuestCount: number;
  unclaimedGuests: NonNullable<GuestListProps["unclaimedGuests"]>;
  isTicketed: boolean;
  isHidden: boolean;
  isAttending: boolean;
  atCapacity: boolean;
  waitlistsEnabled: boolean;
  waitlistSize: number;
  capacity: number;
  onViewAll: () => void;
  ticketsLink?: LinkProps["to"];
  ticketsDisabled?: boolean;
  onRsvp?: () => void;
  onJoinWaitlist?: () => void;
  onSeeOtherPlans?: () => void;
  className?: string;
};

function InlineGuestList({
  attendees,
  unclaimedGuestCount,
  unclaimedGuests,
  isTicketed,
  isHidden,
  isAttending,
  atCapacity,
  waitlistsEnabled,
  waitlistSize,
  capacity,
  onViewAll,
  ticketsLink,
  ticketsDisabled = false,
  onRsvp,
  onJoinWaitlist,
  onSeeOtherPlans,
  className = "",
}: InlineGuestListProps) {
  const locale = useRootLocale();
  const attendeesCount = attendees.length + unclaimedGuestCount;
  const unclaimedAvatars = Math.min(unclaimedGuestCount, Math.max(0, 9 - attendees.length));
  const placesLeft = Math.max(0, capacity - attendeesCount);
  const showSpotsLeft = placesLeft > 0 && placesLeft <= 5;

  const countParts: string[] = [];
  if (atCapacity && isTicketed) {
    countParts.push("Sold out");
  } else if (atCapacity && !isTicketed) {
    countParts.push(
      attendeesCount === 1
        ? "1 person going"
        : `${attendeesCount} people going`,
    );
  }
  if (waitlistSize > 0) {
    countParts.push(
      waitlistSize === 1
        ? "1 person waitlisted"
        : `${waitlistSize} people waitlisted`,
    );
  }
  if (!atCapacity && showSpotsLeft) {
    countParts.push(formatOnlyPlacesLeft(placesLeft, locale));
  }

  const othersCount = Math.max(0, attendeesCount - 1);
  const goingParts: { text: string; bold: boolean }[] = [];
  if (othersCount > 0) {
    goingParts.push({
      text: `${othersCount} other${othersCount > 1 ? "s" : ""}`,
      bold: false,
    });
  }
  if (placesLeft > 0) {
    goingParts.push({
      text: formatPlacesLeft(placesLeft, locale),
      bold: true,
    });
  }
  if (waitlistSize > 0) {
    goingParts.push({ text: `${waitlistSize} waitlisted`, bold: false });
  }

  const showJoinWaitlistCta =
    !ticketsDisabled &&
    !isAttending &&
    atCapacity &&
    waitlistsEnabled &&
    !!onJoinWaitlist;
  const showSeeOtherPlansCta =
    !ticketsDisabled &&
    !isAttending &&
    atCapacity &&
    !waitlistsEnabled &&
    !!onSeeOtherPlans;

  return (
    <section className={`flex flex-col gap-4 ${className}`}>
      <SectionHeading>Guest list</SectionHeading>

      {isHidden ? (
        <button
          type="button"
          onClick={onViewAll}
          aria-label="See why the guest list is private"
          className="flex items-center pr-1.5 cursor-pointer transition-opacity hover:opacity-80 focus-visible:outline-2 focus-visible:outline-butter-green rounded-full"
        >
          {FAKE_PROFILES.map((profile, i) => (
            <img
              key={`${profile.name}-${i}`}
              src={profile.src}
              alt=""
              aria-hidden
              className="-mr-1.5 h-9 w-9 flex-shrink-0 rounded-full border border-butter-cream-light object-cover blur-[2px]"
            />
          ))}
        </button>
      ) : attendeesCount > 0 ? (
        <div className="flex -space-x-2">
          {attendees.slice(0, 9).map((guest) => (
            <Avatar
              key={guest.node.id}
              user={guest.node.user}
              size={36}
              rounded
              className="border-2 border-butter-cream-light"
            />
          ))}
          {Array.from({ length: unclaimedAvatars }, (_, index) => (
            <div
              key={unclaimedGuests[index]?.id ?? `unclaimed-${index}`}
              role="img"
              aria-label={unclaimedGuests[index] ? `${unclaimedGuests[index].name}, unclaimed` : "Unclaimed guest place"}
            >
              <Avatar user={{ name: unclaimedGuests[index]?.name }} size={36} rounded className="border-2 border-butter-cream-light" />
            </div>
          ))}
          {attendeesCount > 9 && (
            <div className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-butter-cream-light bg-butter-green">
              <Text
                as="span"
                variant="secondary"
                size="xs"
                weight="bold"
                color="light"
              >
                +{attendeesCount - 9}
              </Text>
            </div>
          )}
        </div>
      ) : null}

      {isAttending ? (
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
          <FiCheck size={16} aria-hidden className="text-butter-green" />
          <Text as="span" variant="secondary" size="md" weight="semibold">
            You&apos;re going
          </Text>
          {goingParts.map((part, i) => (
            <Text
              as="span"
              key={i}
              variant="secondary"
              size="md"
              weight={part.bold ? "bold" : "regular"}
            >
              • {part.text}
            </Text>
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          {countParts.length > 0 && (
            <Text as="span" variant="secondary" size="md">
              {countParts.map((part, i) => (
                <span key={i}>
                  {i > 0 && (
                    <span aria-hidden className="mx-1 font-bold">
                      ·
                    </span>
                  )}
                  {part}
                </span>
              ))}
            </Text>
          )}
          {showJoinWaitlistCta ? (
            <PillButton
              type="button"
              onClick={onJoinWaitlist}
              variant="green-soft"
              size="sm"
              rightIcon={<FiChevronRight size={16} aria-hidden />}
              className="!bg-white !text-butter-pink font-normal"
            >
              Join the waitlist
            </PillButton>
          ) : showSeeOtherPlansCta ? (
            <PillButton
              type="button"
              onClick={onSeeOtherPlans}
              variant="green-soft"
              size="sm"
              rightIcon={<FiChevronRight size={16} aria-hidden />}
              className="!bg-white !text-butter-pink font-normal"
            >
              See what else is on
            </PillButton>
          ) : isTicketed && !isHidden && ticketsLink ? (
            <PillButton
              to={ticketsLink}
              variant="green-soft"
              size="sm"
              rightIcon={<FiChevronRight size={16} aria-hidden />}
              className="!bg-white !text-butter-pink font-normal"
            >
              Get tickets
            </PillButton>
          ) : isHidden ? (
            ticketsLink ? (
              <PillButton
                to={ticketsLink}
                variant="green-soft"
                size="sm"
                rightIcon={<FiChevronRight size={16} aria-hidden />}
                className="!bg-white !text-butter-pink font-normal"
              >
                Get tickets to see who&apos;s going
              </PillButton>
            ) : (
              <PillButton
                type="button"
                onClick={ticketsDisabled ? undefined : onViewAll}
                disabled={ticketsDisabled}
                variant="green-soft"
                size="sm"
                rightIcon={<FiChevronRight size={16} aria-hidden />}
                className="!bg-white !text-butter-pink font-normal disabled:opacity-50 disabled:cursor-default disabled:hover:scale-100"
              >
                {ticketsDisabled
                  ? "Get tickets"
                  : "Get tickets to see who's going"}
              </PillButton>
            )
          ) : (
            !isHidden &&
            onRsvp && (
              <PillButton
                type="button"
                onClick={onRsvp}
                variant="green-soft"
                size="sm"
                rightIcon={<FiChevronRight size={16} aria-hidden />}
                className="!bg-white !text-butter-pink font-normal"
              >
                RSVP to see who&apos;s going
              </PillButton>
            )
          )}
        </div>
      )}
    </section>
  );
}
